import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Input,
  Button,
  Space,
  Tag,
  Avatar,
  Typography,
  Modal,
  Form,
  message,
  Popconfirm,
  Select,
  Row,
  Col,
  Descriptions,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { usersService, User, UserFilters } from '../services/usersService';
import { getImageUrl } from '../config/api';
import './Users.css';

const { Title } = Typography;
const { Option } = Select;

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdOn',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching users with filters:', filters);
      const response = await usersService.getUsers(filters);
      console.log('✅ Users response:', response);
      
      // Handle the response structure: { users: [], pagination: {} }
      if (response && response.users) {
        console.log(`📊 Setting ${response.users.length} users`);
        setUsers(response.users);
        if (response.pagination) {
          setPagination({
            current: response.pagination.currentPage || filters.page || 1,
            pageSize: response.pagination.itemsPerPage || filters.limit || 20,
            total: response.pagination.totalItems || 0,
          });
        }
      } else if (Array.isArray(response)) {
        console.log(`📊 Setting ${response.length} users (array response)`);
        setUsers(response);
      } else {
        console.warn('⚠️ Unexpected response format:', response);
        setUsers([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching users:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch users';
      message.error(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    form.setFieldsValue(user);
    setEditModalVisible(true);
  };

  const handleUpdate = async (values: any) => {
    if (!selectedUser) return;
    
    try {
      await usersService.updateUser(selectedUser._id, values);
      message.success('User updated successfully');
      setEditModalVisible(false);
      setSelectedUser(null);
      form.resetFields();
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await usersService.deleteUser(id);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete user');
    }
  };

  const handleToggleVerification = async (id: string) => {
    try {
      await usersService.toggleVerification(id);
      message.success('User verification status updated');
      fetchUsers();
    } catch (error: any) {
      message.error(error.message || 'Failed to update verification status');
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (record: User) => (
        <Space>
          <Avatar src={getImageUrl(record.profileImage || '')} icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 500 }}>
              <a onClick={() => navigate(`/users/${record._id}`)} style={{ cursor: 'pointer', color: '#1890ff' }}>
                {record.name}
              </a>
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>@{record.username}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string) => email ? (
        <span style={{ wordBreak: 'break-word' }}>{email}</span>
      ) : '-',
    },
    {
      title: 'Phone',
      key: 'phone',
      width: 150,
      render: (record: User) => {
        if (record.countryCode && record.phone) {
          return `${record.countryCode}${record.phone}`;
        }
        return record.phone || '-';
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (record: User) => (
        <Space wrap size={[4, 4]}>
          {record.isAdmin && <Tag color="purple">Admin</Tag>}
          {record.isVerified && <Tag color="green">Verified</Tag>}
          {record.isPrivate && <Tag color="orange">Private</Tag>}
        </Space>
      ),
    },
    {
      title: 'Stats',
      key: 'stats',
      width: 150,
      render: (record: User) => (
        <Space direction="vertical" size="small" style={{ fontSize: 12 }}>
          <span>Posts: {record.postsCount || 0}</span>
          <span>Followers: {record.followersCount || 0}</span>
          <span>Following: {record.followingCount || 0}</span>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 120,
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (record: User) => (
        <Space direction="vertical" size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ padding: 0 }}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={record.isVerified ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleVerification(record._id)}
            style={{ padding: 0 }}
          >
            {record.isVerified ? 'Unverify' : 'Verify'}
          </Button>
          {!record.isAdmin && (
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDelete(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} style={{ padding: 0 }}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const renderUserCard = (user: User) => (
    <Card key={user._id} className="user-card">
      <div className="user-card-header">
        <Avatar src={getImageUrl(user.profileImage || '')} icon={<UserOutlined />} size={64} />
        <div className="user-card-info">
          <div className="user-card-name">{user.name}</div>
          <div className="user-card-username">@{user.username}</div>
        </div>
      </div>

      <Descriptions column={1} size="small" className="user-card-details">
        {user.email && (
          <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
        )}
        {(user.countryCode || user.phone) && (
          <Descriptions.Item label="Phone">
            {user.countryCode && user.phone ? `${user.countryCode}${user.phone}` : user.phone || '-'}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Status">
          <Space wrap size={[4, 4]}>
            {user.isAdmin && <Tag color="purple">Admin</Tag>}
            {user.isVerified && <Tag color="green">Verified</Tag>}
            {user.isPrivate && <Tag color="orange">Private</Tag>}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Stats">
          Posts: {user.postsCount || 0} | Followers: {user.followersCount || 0} | Following: {user.followingCount || 0}
        </Descriptions.Item>
        {user.createdOn && (
          <Descriptions.Item label="Created">
            {new Date(user.createdOn).toLocaleDateString()}
          </Descriptions.Item>
        )}
      </Descriptions>

      <div className="user-card-actions">
        <Button
          icon={<EditOutlined />}
          onClick={() => handleEdit(user)}
        >
          Edit
        </Button>
        <Button
          icon={user.isVerified ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
          onClick={() => handleToggleVerification(user._id)}
        >
          {user.isVerified ? 'Unverify' : 'Verify'}
        </Button>
        {!user.isAdmin && (
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(user._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>
    </Card>
  );

  return (
    <div className="users-page">
      <Card>
        <Row justify="space-between" align="middle" className="users-header" gutter={[16, 16]}>
          <Col xs={24} sm={24} md={8}>
            <Title level={2} style={{ margin: 0 }}>Users</Title>
          </Col>
          <Col xs={24} sm={24} md={16}>
            <div className="users-filters">
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined />}
                style={{ flex: 1, minWidth: 150 }}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              />
              <Select
                style={{ width: 120 }}
                placeholder="Verified"
                allowClear
                onChange={(value) => setFilters({ ...filters, isVerified: value, page: 1 })}
              >
                <Option value={true}>Verified</Option>
                <Option value={false}>Not Verified</Option>
              </Select>
              <Select
                style={{ width: 120 }}
                placeholder="Admin"
                allowClear
                onChange={(value) => setFilters({ ...filters, isAdmin: value, page: 1 })}
              >
                <Option value={true}>Admin</Option>
                <Option value={false}>User</Option>
              </Select>
              <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
                Refresh
              </Button>
            </div>
          </Col>
        </Row>

        {/* Desktop Table View */}
        <div className="users-table-desktop">
          <Table
            columns={columns}
            dataSource={users}
            rowKey="_id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setFilters({ ...filters, page, limit: pageSize });
                setPagination({ ...pagination, current: page, pageSize });
              },
            }}
          />
        </div>

        {/* Mobile Card View */}
        <div className="users-table-mobile">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Typography.Text>Loading users...</Typography.Text>
            </div>
          ) : users.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Typography.Text>No users found</Typography.Text>
            </div>
          ) : (
            <>
              {users.map(renderUserCard)}
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Space>
                  <Button
                    disabled={pagination.current === 1}
                    onClick={() => {
                      const newPage = pagination.current - 1;
                      setFilters({ ...filters, page: newPage });
                      setPagination({ ...pagination, current: newPage });
                    }}
                  >
                    Previous
                  </Button>
                  <Typography.Text>
                    Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
                  </Typography.Text>
                  <Button
                    disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                    onClick={() => {
                      const newPage = pagination.current + 1;
                      setFilters({ ...filters, page: newPage });
                      setPagination({ ...pagination, current: newPage });
                    }}
                  >
                    Next
                  </Button>
                </Space>
                <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                  Showing {((pagination.current - 1) * pagination.pageSize) + 1} - {Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total} users
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedUser(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width="90%"
        style={{ maxWidth: 500 }}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="bio" label="Bio">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;

